import {
  invalid,
  readBoolean,
  readBoundedNumber,
  readCount,
  readMoney,
  readRecord,
  readString,
} from '../readers';
import type {
  CatalogSearchResponse,
  CatalogSearchResult,
  HighlightField,
} from '../../data/catalogSearch';
import type {
  DiscoveryApi,
  DiscoveryExecution,
  DiscoveryHealth,
  DiscoveryHttpMethod,
  DiscoveryLane,
  DiscoveryLatency,
  DiscoveryPricing,
  DiscoverySource,
  PricingAddon,
  PricingOffer,
} from '../../data/discovery';

const providerAt = (value: unknown, path: string): 'AnyAPI' => {
  if (value !== 'AnyAPI') return invalid(path);
  return 'AnyAPI';
};

const methodAt = (value: unknown, path: string): DiscoveryHttpMethod => {
  const method = readString(value, path);
  if (method !== 'POST') return invalid(path);
  return method;
};

const routePathAt = (value: unknown, path: string): string => {
  const route = readString(value, path);
  if (!route.startsWith('/') || route.startsWith('//')) return invalid(path);
  return route;
};

const executionAt = (value: unknown, path: string): DiscoveryExecution => {
  const raw = readRecord(value, path);
  const mode = readString(raw.mode, `${path}.mode`);
  if (mode !== 'sync' && mode !== 'durable') return invalid(`${path}.mode`);
  return { mode };
};

const assertCustomerSafe = (value: unknown, path: string): void => {
  if (Array.isArray(value)) {
    value.forEach((item, index) => assertCustomerSafe(item, `${path}[${index}]`));
    return;
  }
  if (value === null || typeof value !== 'object') return;
  for (const [key, child] of Object.entries(value)) {
    if (key.toLowerCase().includes('credit')) invalid(`${path}.${key}`);
    if (key === 'provider' && child !== 'AnyAPI') invalid(`${path}.${key}`);
    assertCustomerSafe(child, `${path}.${key}`);
  }
};

const offerAt = (value: unknown, path: string): PricingOffer => {
  const raw = readRecord(value, path);
  const model = readString(raw.model, `${path}.model`);
  const unit = readString(raw.unit, `${path}.unit`);
  const maxUsd = readMoney(raw.maxUsd, `${path}.maxUsd`);
  if (model === 'flat') {
    if (unit !== 'request') invalid(`${path}.unit`);
    return { model: 'flat', unit: 'request', maxUsd };
  }
  if (model !== 'linear' || unit.length === 0) invalid(`${path}.model`);
  return {
    model: 'linear',
    unit,
    baseUsd: readMoney(raw.baseUsd, `${path}.baseUsd`),
    perUnitUsd: readMoney(raw.perUnitUsd, `${path}.perUnitUsd`),
    maxUsd,
    ...(raw.addons === undefined ? {} : { addons: addonsAt(raw.addons, `${path}.addons`) }),
  };
};

/**
 * Optional extras a caller switches on with an input field.
 *
 * Absent is the normal case - almost no offer sells one - and an absent key must
 * read as "no extras", never as malformed, because this reader also runs against
 * a gateway deployed before the field existed. A PRESENT but malformed block is
 * still a hard failure, like every other field here: a price we cannot parse is
 * not a price we may guess at.
 */
const addonsAt = (value: unknown, path: string): PricingAddon[] => {
  if (!Array.isArray(value)) invalid(path);
  return (value as unknown[]).map((entry, index) => {
    const raw = readRecord(entry, `${path}[${index}]`);
    return {
      id: readString(raw.id, `${path}[${index}].id`),
      label: readString(raw.label, `${path}[${index}].label`),
      field: readString(raw.field, `${path}[${index}].field`),
      usd: readMoney(raw.usd, `${path}[${index}].usd`),
    };
  });
};

const pricingAt = (value: unknown, path: string): DiscoveryPricing => {
  const raw = readRecord(value, path);
  return {
    from: offerAt(raw.from, `${path}.from`),
    failoverMaxUsd: readMoney(raw.failoverMaxUsd, `${path}.failoverMaxUsd`),
  };
};

const healthAt = (value: unknown, path: string): DiscoveryHealth => {
  const raw = readRecord(value, path);
  return {
    window: readString(raw.window, `${path}.window`),
    uptimePct: readBoundedNumber(raw.uptimePct, `${path}.uptimePct`, undefined, 100),
    latencyP50Ms: readCount(raw.latencyP50Ms, `${path}.latencyP50Ms`),
    uptimeSample: readCount(raw.uptimeSample, `${path}.uptimeSample`),
    latencySample: readCount(raw.latencySample, `${path}.latencySample`),
    requests: readCount(raw.requests, `${path}.requests`),
    servedRequests: readCount(raw.servedRequests, `${path}.servedRequests`),
    ...(raw.blockedPct === undefined
      ? {}
      : { blockedPct: readBoundedNumber(raw.blockedPct, `${path}.blockedPct`, undefined, 100) }),
  };
};

const latencyAt = (value: unknown, path: string): DiscoveryLatency => {
  const raw = readRecord(value, path);
  const basis = readString(raw.basis, `${path}.basis`);
  if (basis !== 'service_time_excludes_caller_requested_delay') {
    return invalid(`${path}.basis`);
  }
  const sample = readCount(raw.sample, `${path}.sample`, 1);
  return {
    window: readString(raw.window, `${path}.window`),
    p50Ms: readCount(raw.p50Ms, `${path}.p50Ms`),
    p95Ms: readCount(raw.p95Ms, `${path}.p95Ms`),
    p99Ms: readCount(raw.p99Ms, `${path}.p99Ms`),
    sample,
    basis,
  };
};

const sourceAt = (value: unknown, path: string): DiscoverySource => {
  const raw = readRecord(value, path);
  const kind = readString(raw.kind, `${path}.kind`);
  if (kind !== 'anonymous' && kind !== 'brand') invalid(`${path}.kind`);
  return {
    id: readString(raw.id, `${path}.id`),
    name: readString(raw.name, `${path}.name`),
    kind: kind as DiscoverySource['kind'],
    artworkKey: readString(raw.artworkKey, `${path}.artworkKey`),
  };
};

const laneAt = (value: unknown, path: string): DiscoveryLane => {
  const raw = readRecord(value, path);
  return {
    pricing: offerAt(raw.pricing, `${path}.pricing`),
    ...(raw.health === undefined ? {} : { health: healthAt(raw.health, `${path}.health`) }),
    source: sourceAt(raw.source, `${path}.source`),
  };
};

/** The withdrawn sources, when the gateway publishes any. Absent is the normal
 *  case and must read as "every source is serving", never as malformed: this
 *  reader also runs against a gateway deployed before the key existed. */
const notServingAt = (value: unknown, path: string): DiscoveryLane[] => {
  if (!Array.isArray(value)) invalid(path);
  return (value as unknown[]).map((lane, index) => laneAt(lane, `${path}[${index}]`));
};

const optionalBoolean = (
  raw: Record<string, unknown>,
  key: string,
  path: string,
): Record<string, boolean> =>
  raw[key] === undefined ? {} : { [key]: readBoolean(raw[key], `${path}.${key}`) };

const optionalTrue = (
  raw: Record<string, unknown>,
  key: string,
  path: string,
): Record<string, true> => {
  if (raw[key] === undefined) return {};
  if (raw[key] !== true) return invalid(`${path}.${key}`);
  return { [key]: true };
};

const optionalPositiveInteger = (
  raw: Record<string, unknown>,
  key: string,
  path: string,
): Record<string, number> => {
  if (raw[key] === undefined) return {};
  return { [key]: readCount(raw[key], `${path}.${key}`, 1) };
};

const operationFieldsAt = (raw: Record<string, unknown>, path: string) => ({
  slug: readString(raw.slug, `${path}.slug`),
  category: readString(raw.category, `${path}.category`) as CatalogSearchResult['category'],
  name: readString(raw.name, `${path}.name`),
  description: readString(raw.description, `${path}.description`),
  method: methodAt(raw.method, `${path}.method`),
  path: routePathAt(raw.path, `${path}.path`),
  execution: executionAt(raw.execution, `${path}.execution`),
  provider: providerAt(raw.provider, `${path}.provider`),
  pricing: pricingAt(raw.pricing, `${path}.pricing`),
  ...optionalPositiveInteger(raw, 'tryMaxItems', path),
  failover: readBoolean(raw.failover, `${path}.failover`),
  ...optionalTrue(raw, 'excludesCallerDelay', path),
});

const schemaAt = (value: unknown, path: string, required: boolean): unknown => {
  if (value === undefined && !required) return undefined;
  return readRecord(value, path);
};

const projectApi = (value: unknown, path: string, requireSchemas: boolean): DiscoveryApi => {
  const raw = readRecord(value, path);
  if (!Array.isArray(raw.lanes)) invalid(`${path}.lanes`);
  const lanes = raw.lanes as unknown[];
  if (lanes.length === 0) invalid(`${path}.lanes`);
  const inputSchema = schemaAt(raw.inputSchema, `${path}.inputSchema`, requireSchemas);
  const outputSchema = schemaAt(raw.outputSchema, `${path}.outputSchema`, requireSchemas);
  return {
    id: readString(raw.id, `${path}.id`),
    ...operationFieldsAt(raw, path),
    lanes: lanes.map((lane, index) => laneAt(lane, `${path}.lanes[${index}]`)),
    ...(raw.notServingSources === undefined
      ? {}
      : {
          notServingSources: notServingAt(
            raw.notServingSources,
            `${path}.notServingSources`,
          ),
        }),
    ...(inputSchema === undefined ? {} : { inputSchema }),
    ...(outputSchema === undefined ? {} : { outputSchema }),
    ...optionalBoolean(raw, 'heavy', path),
    ...optionalBoolean(raw, 'tryEligible', path),
  };
};

const highlightAt = (value: unknown, path: string): HighlightField => {
  const raw = readRecord(value, path);
  return {
    path: readString(raw.path, `${path}.path`),
    type: readString(raw.type, `${path}.type`),
    ...(raw.why === undefined ? {} : { why: readString(raw.why, `${path}.why`) }),
  };
};

const searchResultAt = (value: unknown, path: string): CatalogSearchResult => {
  const raw = readRecord(value, path);
  const rawHighlights = raw.highlightFields;
  if (rawHighlights !== undefined && !Array.isArray(rawHighlights)) {
    invalid(`${path}.highlightFields`);
  }
  const highlights = Array.isArray(rawHighlights) ? rawHighlights : undefined;
  return {
    ...operationFieldsAt(raw, path),
    platformId: readString(raw.platformId, `${path}.platformId`),
    relevance: readBoundedNumber(raw.relevance, `${path}.relevance`, 0, 1),
    ...(highlights === undefined
      ? {}
      : {
          highlightFields: highlights.map((item, index) =>
            highlightAt(item, `${path}.highlightFields[${index}]`)),
        }),
  };
};

export const readDiscoveryList = (value: unknown): DiscoveryApi[] => {
  assertCustomerSafe(value, 'catalog');
  const raw = readRecord(value, 'catalog');
  if (!Array.isArray(raw.apis)) invalid('catalog.apis');
  const apis = raw.apis as unknown[];
  return apis.map((api, index) => projectApi(api, `catalog.apis[${index}]`, false));
};

export const readDiscoveryDetail = (value: unknown): DiscoveryApi => {
  assertCustomerSafe(value, 'api');
  const raw = readRecord(value, 'api');
  const detail = projectApi(raw, 'api', true);
  if (raw.latency === undefined) return detail;
  return {
    ...detail,
    latency: raw.latency === null ? null : latencyAt(raw.latency, 'api.latency'),
  };
};

export const readDiscoverySearch = (value: unknown): CatalogSearchResponse => {
  assertCustomerSafe(value, 'search');
  const raw = readRecord(value, 'search');
  if (!Array.isArray(raw.results)) invalid('search.results');
  const results = raw.results as unknown[];
  const rawRanking = readString(raw.ranking, 'search.ranking');
  if (rawRanking !== 'semantic' && rawRanking !== 'keyword') invalid('search.ranking');
  const ranking = rawRanking as CatalogSearchResponse['ranking'];
  return {
    results: results.map((result, index) =>
      searchResultAt(result, `search.results[${index}]`)),
    total: readCount(raw.total, 'search.total'),
    ranking,
  };
};
